import type { LanguageStrings } from '../types_i18n';

export const strings: Record<string, LanguageStrings> = {
  en: {
    welcome: 'Welcome',
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Success!',
    next: 'Next',
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    navigation: {
      dashboard: 'Dashboard',
      practice: 'Practice',
      mockExam: 'Mock Exam',
      chat: 'Chat',
      settings: 'Settings',
      coach: 'Report',
    },
    dashboard: {
      title: 'Dashboard',
      welcomeMessage: "Welcome back!",
      welcomeSubtitle: "Let's make learning fun!",
      startPractice: "Start",
      performanceTracker: "Performance",
      testsCompleted: "Tests Conquered",
      averageScore: "Your Super Score",
      categoryPerformance: "Skill-o-Meter",
      focusAreas: "Power-Ups Needed",
      focusNote: "Practice these to level up!",
      upgradeToPremium: "Unlock Epic Mode",
      upgradeDescription: "Get all tests, all features, all the fun!",
      upgradeNow: "Upgrade & Zoom",
      doingAmazing: "You're zooming ahead! Keep it up!",
      makingProgress: "Every click is a win!",
      readyToStart: "Adventure awaits!",
      woohoo: "Woohoo! You're a star!",
      youGotThis: "You've got this! Smash it!",
      firstWin: "First win unlocked!",
      examReadiness: "Exam Readiness",
      yourJourney: "Your Journey",
      studyTime: "Studied",
      timeRemaining: "Remaining",
      yourProgress: "Continue Learning",
      gamification: {
        streak: "{days}-day streak",
        streakAmazing: "{days}-day streak - Amazing!",
        level: "Level {level} - {kp} KP",
        levelProgress: "Level {level} ({progress}% to next)",
        todayProgress: "{progress} questions today",
        startStreak: "Start your streak today!",
        firstGoal: "Complete 1 practice test",
        takeTestsToIdentify: "Take tests to identify focus areas",
        almostDone: "{progress} - Almost there!",
        levelBeginner: "Level {level} - Beginner"
      },
      todayFocus: "Today's Focus",
      focusOnThis: "Focus on this area to improve your overall readiness",
      startMockExam: "Start Mock Exam",
      readyToTest: "Ready to test your knowledge!",
      notPracticedYet: "Not practiced yet - good next step",
      readyToStartLearning: "Ready to start learning",
      startWithBasics: "Start with the basics and build up!",
      practiceMore: "Practice More",
      continueBuilding: "Continue building your foundation",
      excellentMastery: "excellent mastery",
      strongPerformance: "strong performance",
      goodProgress: "good progress",
      buildingSkills: "building skills",
      loadingProgress: "Loading your progress...",
      analyzingData: "Analyzing your study data and generating insights",
      recommendation: "Recommendation",
      mockExamsAvailable: "Mock Exams Available",
      allTestsCompleted: "All practice tests completed! You can now take mock exams.",
      startFirstTest: "Start with your first practice test!",
      focus: "Focus",
      strength: "Strength",
      noWeakAreas: "No weak areas identified yet. Keep practicing!",
      maintainStrength: "Keep maintaining this strength!",
      completeTestsForStrength: "Complete practice tests to see your strong areas.",
      aiAdaptive: "AI Adaptive",
      weakAreaFocus: "Weak Area Focus",
      aiPersonalized: "AI Personalized",
      goalAchieved: "Goal Achieved!",
      goalProgress: "complete - {time} remaining",
      aiIdentifiedWeakArea: "AI identified this as your weakest area ({score}% score)",
      aiRecommendsFocus: "AI recommends focusing here first",
      currentScore: "Current Score",
      targetScore: "Target: 80%",
      toGo: "{percent}% to go",
      personalizedExamInfo: "Your mock exams are automatically personalized based on your weak areas. Each exam focuses on topics where you need the most practice, ensuring maximum learning efficiency.",
      examFocusedOn: "Focused on your weak areas:",
      examTailored: "This exam is tailored to help you improve where you need it most",
      recommendedNext: "Recommended Next",
      memoryContext: {
        struggledToday: "Struggled today ({score}%)",
        struggledYesterday: "Struggled yesterday ({score}%)",
        struggledDaysAgo: "Struggled {days} days ago ({score}%)"
      }
    },
    settings: {
      title: 'Settings',
      subscription: 'Subscriptions',
      account: 'Account',
      language: 'Language',
      privacy: 'Privacy',
      terms: 'Terms',
      faq: 'FAQ',
      support: 'Support',
      feedback: 'Feedback',
      about: 'About',
      enterUsername: 'Enter username',
      supportSubject: 'What do you need help with?',
      supportMessage: 'Describe your issue or question...',
      faqData: {
        progressTracking: {
          question: 'How does the progress tracking work?',
          answer: 'Your progress is automatically saved as you complete practice tests. You can view detailed analytics including your average score, weak areas, and study streak. Data is synced across all your devices when you create an account.'
        },
        dataSecurity: {
          question: 'Is my data secure and private?',
          answer: 'Yes! Your data is stored securely in the cloud and is only accessible to you. We don\'t share your personal information with third parties. You can delete your account and all data at any time.'
        },
        offlineUse: {
          question: 'Can I use the app offline?',
          answer: 'Yes, you can use the app offline for practice tests. Your progress will be saved locally and synced to the cloud when you\'re back online. However, some features like cloud sync require an internet connection.'
        },
        aiCoach: {
          question: 'How accurate is the AI Learning Coach?',
          answer: 'The AI Learning Coach analyzes your performance patterns to provide personalized study recommendations. It identifies your weak areas and tracks your learning progress to help you study more effectively.'
        },
        dataLoss: {
          question: 'What if I lose my device or clear my browser?',
          answer: 'If you\'ve created an account, your progress is safely stored in the cloud and can be restored on any device. Simply log in with your name and all your data will be available. For anonymous users, data is stored locally and may be lost if the browser is cleared.'
        }
      }
    },
    mockExam: {
      mockExamName: 'Standard Exam',
      mockExamDescription: 'Fixed structure, mirrors real exam.',
      personalisedExamName: 'AI Personalised Exam',
      personalisedExamDescription: 'Personalised, mirrors real exam, focuses on weaker areas.',
      aiPersonalizedTitle: 'AI Personalized Mock Exams',
      aiPersonalizedDescription: 'Your mock exams are automatically personalized based on your weak areas. Each exam focuses on topics where you need the most practice, ensuring maximum learning efficiency.',
      aiPersonalized: 'AI Personalized',
      upgradeRealMock: 'Upgrade to Premium for unlimited Standard Exams',
      upgradeAI: 'Upgrade to Premium for unlimited AI Personalised Exams',
      unlockedTitle: 'Mock Exams Unlocked!',
      unlockedMessage: "Congratulations! You've completed enough practice tests with good scores to unlock mock exams.",
      testsCompleted: 'tests completed',
      averageScore: 'average score',
      allTestsAbove70: 'All tests above 70%',
      studyTime: 'study time',
      startMockExams: 'Start Mock Exams',
      aiExamScore: 'AI Exam Score',
      latestScore: 'Your latest exam score',
      noScoreYet: 'Complete exam to see your score',
      aiExamExplanation1: 'Personalized exam focusing on your weak areas',
      aiExamExplanation2: 'Uses real official questions with adaptive difficulty',
      aiExamExplanation3: 'Unlimited unique attempts - each exam is different'
    },
    coach: {
      back: 'Back',
      reportTitle: 'Coach\'s Report',
      readiness: 'Exam Confidence',
      accuracy: 'Accuracy',
      coverage: 'Coverage',
      consistency: 'Consistency',
      trend: 'Trend',
      strengths: 'STRENGTHS',
      improvements: 'IMPROVEMENTS',
      recommendations: 'RECOMMENDATIONS',
      tests: 'tests',
      gap: 'Gap',
      impact: 'Impact',
      action: 'Action',
      expected: 'Expected',
      priority: 'Priority',
      notAvailable: 'Coach\'s Report Not Available',
      requirements: 'Complete all practice tests and the AI Exam to unlock your comprehensive Coach\'s Report.',
      practiceTests: 'Practice Tests',
      aiExam: 'AI Exam',
      completed: 'Completed',
      notCompleted: 'Not Completed',
      completePractice: 'Complete Practice Tests',
      startAIExam: 'Start AI Exam',
      yourProgress: 'Your Progress',
      testsCompleted: 'Tests Completed',
      averageScore: 'Average Score',
      readinessScore: 'Exam Confidence',
      examConfidence: 'Exam Confidence'
    },
    achievements: {
      firstTest: {
        title: "First Steps!",
        description: "Completed your first practice test"
      },
      oneHour: {
        title: "Dedicated Learner!",
        description: "Studied for 1 hour"
      },
      fiveHours: {
        title: "Study Champion!",
        description: "Studied for 5 hours"
      },
      seventyPercent: {
        title: "Passing Grade!",
        description: "Achieved 70% average score"
      },
      eightyFivePercent: {
        title: "Exam Ready!",
        description: "Achieved 85% average score"
      },
      firstMock: {
        title: "Mock Exam Master!",
        description: "Completed your first mock exam"
      },
      hundredQuestions: {
        title: "Question Master!",
        description: "Answered 100 questions"
      },
      fiveHundredQuestions: {
        title: "Theory Expert!",
        description: "Answered 500 questions"
      }
    },
    testNames: {
      trafficLightsSignals: "Traffic Lights & Signals",
      priorityRules: "Priority & Right of Way",
      hazardPerception: "Hazard Perception",
      speedSafety: "Speed & Safety",
      bicycleInteractions: "Bicycle Interactions",
      roundaboutRules: "Roundabout Rules",
      tramInteractions: "Tram Interactions",
      pedestrianCrossings: "Pedestrian Crossings",
      constructionZones: "Construction Zones",
      weatherConditions: "Weather Conditions",
      roadSigns: "Road Signs",
      mandatorySigns: "Mandatory Signs",
      prohibitorySigns: "Prohibitory Signs",
      prohibitorySigns2: "Prohibitory Signs 2",
      warningSigns: "Warning Signs",
      roadInformation: "Road Information",
      motorwayRules: "Motorway Rules",
      vehicleKnowledge: "Vehicle Knowledge",
      parkingRules: "Parking Rules",
      environmental: "Environmental Zones",
      technologySafety: "Technology & Safety",
      adasSafety: "ADAS",
      alcoholDrugs: "Alcohol & Drugs",
      fatigueRest: "Fatigue & Rest",
      emergencyProcedures: "Emergency Procedures",
      insightPractice: "Insight Practice",
      mockTest: "Mock Test",
      roadMarkings: "Road Markings",
      trafficSigns: "Traffic Signs",
      vehicleHandling: "Vehicle Handling",
      riskAwareness: "Risk Awareness",
      nightVisibility: "Night Visibility",
      incidentFirstAid: "Incident & First Aid",
      vehicleCategories: "Vehicle Categories",
      environmentalZones: "Environmental Zones",
      vehicleDocumentation: "Vehicle Documentation",
      specializedDriving: "Specialized Driving",
      motorwayMerging: "Motorway Merging"
    },
    practice: {
      title: 'Practice',
      startTest: 'Start',
      recommendedForYou: 'Recommended for You',
      hazardPerception: 'Hazard Perception',
      insightPractice: 'Insight Practice',
      mandatorySigns: 'Mandatory Signs',
      warningSigns: 'Warning Signs',
      prohibitorySigns: 'Prohibitory Signs',
      prohibitorySigns2: 'Prohibitory Signs 2',
      trafficLightsSignals: 'Traffic Lights & Signals',
      roadInformation: 'Road Information',
      signIdentification: 'Sign Identification',
      priorityRules: 'Priority Rules',
      mandatorySigns2: 'Mandatory Signs 2',
      premium: 'Premium',
      question: 'Question',
      of: 'of',
      timeLeft: 's',
      nextQuestion: 'Next',
      nextTest: 'Next Test',
      retake: 'Retake',
      finishTest: 'Finish',
      proceedToNextQuestion: 'Great questions! Ready to move on? Let\'s continue to the next question.',
      explanation: 'Explanation:',
      listening: 'Listening...',
      sayABCD: 'Select A, B, C, or D',
      voiceMode: 'Answer Mode',
      mute: 'Mute',
      unmute: 'Unmute',
      mockExam: 'Exam',
      chatPlaceholder: 'Ask about Dutch driving theory or this question...',
      sendMessage: 'Send message',
      askAfterExplanation: 'Ask after the explanation',
      typeYourQuestion: 'Type your question...',
      continueToNextQuestion: 'Continue to Next Question',
      coachThinking: 'Coach is thinking...',
      languageHelper: 'You can ask in Dutch, English, or Arabic',
      followUpSuggestion1: 'Why is this rule important?',
      followUpSuggestion2: 'Give me another example',
      testProgress: 'Test {completed} of {total} completed',
      allTestsCompleted: 'All tests completed! You\'re ready for the Mock Exam.',
      readyForMockExam: 'All practice tests completed! Ready for the Mock Exam?',
      startingNextTest: 'Starting next test...',
      mockExamButton: 'Mock Exam',
    },
    mockExamBio: {
      title: 'Exam',
      subtitle: 'Test your knowledge under real exam conditions',
      examOverview: 'Exam Overview',
      questions: '50 Questions',
      questionsDesc: 'Comprehensive coverage of all topics',
      timeLimit: '30 Minutes',
      timeLimitDesc: 'Real exam time limit',
      passRate: '70% Pass Rate',
      passRateDesc: '18/25 correct answers required',
      randomQuestions: 'Random Questions',
      randomQuestionsDesc: 'Different questions each time',
      topicsCovered: 'Topics Covered',
      trafficLights: 'Traffic Lights & Signals',
      roadSigns: 'Road Signs (All Types)',
      hazardPerception: 'Hazard Perception',
      priorityRules: 'Priority Rules',
      roadInformation: 'Road Information',
      insightPractice: 'Insight Practice',
      instructions: 'Instructions',
      instruction1: 'Read each question carefully before answering',
      instruction2: 'Select the best answer from the given options',
      instruction3: 'You can review and change answers before finishing',
      instruction4: 'The exam will automatically submit when time runs out',
      instruction5: 'Results will be shown after completion',
      practiceRequirement: 'Practice Requirement',
      progressComplete: 'Complete',
      readyTitle: "Exam Hero!",
      readyMessage: "You’re quiz-ready! Time to shine.",
      almostTitle: "Almost a legend!",
      almostMessage: "Just a bit more practice and you’ll be unstoppable!",
      practiceTitle: "Warm up first!",
      practiceMessage: "A few more practice runs and you’ll ace it!",
      startMockExam: "Start",
      continuePracticing: "Keep Practicing",
      startPracticing: "Start",
      completedTests: "Completed Tests:",
      noTestsCompleted: "No tests completed yet",
      backToDashboard: "Back to Dashboard"
    },
    quiz: {
      title: 'Exam',
      subtitle: 'Dutch Driving Theory Test',
      officialStyle: 'Official Style Exam',
      questions: 'Questions',
      timeLimit: 'Time Limit',
      passRate: 'Pass Rate',
      testType: 'Test Type',
      computerBased: 'Computer Based',
      variesByCategory: 'Varies by category',
      instructions: 'Exam Instructions',
      instruction1: 'Read each question carefully',
      instruction2: 'Select the best answer from the options',
      instruction3: 'You cannot go back to previous questions',
      instruction4: 'The quiz will automatically submit when time runs out',
      instruction5: 'Results will be shown after completion',
      warning: 'This is an official-style quiz. Make sure you\'re ready before starting.',
      startQuiz: 'Start',
      loading: 'Loading quiz...',
      timeLeft: 'Time left',
      question: 'Question',
      finishQuiz: 'Finish Exam',
      previous: 'Previous',
      next: 'Next',
      cancel: 'Cancel',
      finishQuizConfirm: 'Finish Exam',
      finishQuizMessage: 'Are you sure you want to finish the quiz?',
      answeredQuestions: 'You have answered {answered} out of {total} questions.',
      results: 'Exam Results',
      score: 'Score',
      correct: 'Correct',
      incorrect: 'Incorrect',
      totalQuestions: 'Total Questions',
      percentage: 'Percentage',
      timeSpent: 'Time Spent',
      backToPractice: 'Back to Practice',
      tryAgain: 'Try Again',
      shareResults: 'Share Results',
      congratulations: 'Congratulations!',
      wellDone: 'Well done!',
      keepPracticing: 'Keep practicing!',
      passed: 'Passed',
      failed: 'Failed',
      excellent: 'Excellent Performance!',
      great: 'Great Job!',
      good: 'Good Work!',
      close: 'Almost There!',
      needPractice: 'Need More Practice',
      backToQuizzes: 'Back to Quizzes',
      requirement: 'Requirement:',
      requirementText: 'Score 70% or higher on practice tests to access mock exams',
      questionsLabel: 'questions',
      minutesLabel: 'min',
      passMarkLabel: 'to pass',
      completePracticeToUnlock: 'Complete practice tests to unlock',
      examRules: 'Exam Rules',
      aiExamIntroTitle: 'Test your knowledge',
      aiExamIntroSubtitle: '50 questions · 30 minutes · 80% to pass',
      rule1: 'The examination consists of 50 questions and must be completed within 30 minutes (CBR 2025 format).',
      rule2: 'A minimum of 44 correct answers (88%) is required to pass the examination.',
      rule3: 'Each question contains 3 answer options. Select the one correct answer.',
      rule4: 'Once an answer is submitted, it cannot be changed. This applies throughout the examination.',
      rule5: 'The examination is conducted on a computer and follows the official CBR examination structure and timing requirements.',
      rule6: 'The examination timer continues to run even if the page is closed or refreshed. Manage your time accordingly.',
      aiPersonalizedExam: 'AI Personalized Exam',
      focusedOnWeakAreas: 'Focused on your weak areas:',
      tailoredToHelp: 'This exam is tailored to help you improve where you need it most'
    },
    quizSelection: {
      title: 'Exam',
      subtitle: 'Choose your difficulty level',
      level1: {
        title: 'Beginner Exam',
        subtitle: '',
        description: '',
        passRate: '80% (20/25 correct)'
      },
      level2: {
        title: 'Intermediate Exam',
        subtitle: '',
        description: '',
        passRate: '80% (20/25 correct)'
      },
      level3: {
        title: 'Advanced Exam',
        subtitle: '',
        description: '',
        passRate: '80% (20/25 correct)'
      },
      difficulty: {
        beginner: 'Beginner',
        intermediate: 'Intermediate',
        advanced: 'Advanced'
      },
      questions: 'Questions',
      timeLimit: 'Time Limit',
      passRate: 'Pass Rate',
      startQuiz: 'Start',
      footer: '',
      status: {
        completed: 'Completed',
        attempted: 'Attempted',
        available: 'Available',
        locked: 'Locked'
      },
      bestScore: 'Best Score',
      locked: 'Locked',
      unlockMessage: 'Complete the {level} level first to unlock this quiz.',
      progress: '{completed}/3 levels completed'
    },
    chat: {
      title: 'Chat',
      subtitle: 'Ask any question on driving rules and exams only.',
      placeholder: 'Type your question here...',
      send: 'Send',
      emptyState: "No messages yet. Ask your first driving question!",
      typing: "Thinking up something awesome..."
    },
    results: {
      title: 'Test Results',
      congratulations: "You did it!",
      wellDone: "Epic job!",
      keepPracticing: "Keep going, champion!",
      score: 'Score',
      correct: 'Correct',
      incorrect: 'Incorrect',
      totalQuestions: 'Total Questions',
      percentage: 'Percentage',
      timeSpent: 'Time Spent',
      backToPractice: 'Back to Practice',
      tryAgain: "Give it another go!",
      shareResults: "Brag to your friends! 🥳",
      testComplete: 'Test Complete! You scored {score}/{total} ({percentage}%)',
      excellent: 'Excellent work! You\'re mastering this topic!',
      good: 'Good job! Keep practicing to improve further.',
      keepPracticingMessage: 'Keep practicing! You\'re making progress.',
      whatWouldYouLike: 'What would you like to do?',
      continueNext: 'Next Test',
      retryTest: 'Retry This Test',
      understandOptions: 'I understand. Would you like to continue to the next test, retry this test, or go to the dashboard?',
      typeChoice: 'Type your answer'
    },
    help: {
      needHint: 'Need a hint?',
      showExample: 'Show example scenario',
      relatedTopics: 'Related topics you might want to review:'
    },
    progress: {
      overallProgress: 'Overall Progress',
      milestones: 'Milestones',
      topicStrength: 'Topic Strength'
    },
    voice: {
      listening: 'Listening...',
      confidence: 'Confidence',
      editMessage: 'Edit your message...',
      send: 'Send'
    },
    streak: {
      warningTitle: 'Your streak is at risk!',
      warningMessage: 'You have {minutes} minutes left to keep your {days}-day streak alive!',
      freezeStreak: 'Freeze Streak',
      protected: 'Streak protected — keep going today 🔥',
      signup: 'Sign up',
      signupMessage: 'Sign up to start your streak 🔥'
    },
    leaderboard: {
      title: 'Leaderboard',
      weekly: 'Weekly',
      city: 'City',
      friends: 'Friends',
      yourRank: 'Your rank'
    },
    common: {
      send: 'Send'
    },
    aiTutor: {
      title: 'Coach',
      welcome: 'Hi! I can help with Dutch driving theory, exam costs, traffic rules, and your progress. What would you like to know?',
      error: 'Sorry, I\'m having trouble right now. Please try again in a moment.',
      actionItems: 'Action Items:',
      nextSteps: 'Next Steps:',
      inputPlaceholder: 'Message Coach...',
      quickQuestions: {
        title: 'Try asking:'
      }
    },
    examDate: {
      title: 'When\'s Your Exam?',
      selectDate: 'Select Date',
      skip: 'Skip for now',
      continue: 'Continue',
      continuing: 'Continuing...'
    },
    install: {
      title: 'Install theorycoach',
      description: 'Learn driving theory in 24 hours with our AI-powered coaching system',
      mobileMessage: 'Install on mobile for the best experience',
      installNow: 'Install Now',
      maybeLater: 'Maybe Later'
    },
    progressTracker: {
      title: 'Performance',
      emptyMessage: 'No progress yet. Start your first test!',
      testsCompleted: 'Tests Completed',
      averageScore: 'Average Score',
      dayStreak: 'Day Streak',
      examReady: 'Exam Ready',
      recentPerformance: 'Recent Performance',
      focusAreas: 'Focus Areas',
      strongAreas: 'Strong Areas',
      noWeakAreas: 'No weak spots found.',
      noStrongAreas: 'No strong areas yet.',
      practiceWeakAreas: 'Practice Weak Areas',
      takeMockExam: 'Take Mock Exam',
      improving: 'Improving',
      declining: 'Declining',
    },
    testCategories: {
      hazardPerception: 'Hazard Perception',
      insightPractice: 'Insight Practice',
      mandatorySigns: 'Mandatory Signs',
      warningSigns: 'Warning Signs',
      prohibitorySigns: 'Prohibitory Signs',
      prohibitorySigns2: 'Prohibitory Signs 2',
      trafficLightsSignals: 'Traffic Lights & Signals',
      roadInformation: 'Road Information',
      signIdentification: 'Sign Identification',
      priorityRules: 'Priority Rules',
      mandatorySigns2: 'Mandatory Signs 2',
      mockExam: 'Exam',
    },
    subscription: {
      title: 'Choose a plan',
      subtitle: 'Start free. Unlock more by sharing or go Pro for unlimited power.',
      free: {
        name: 'Free',
        subtitle: 'Best for trying out',
        price: '€0',
        period: 'forever',
        feature1: 'Limited Practice Tests',
        feature2: 'Standard Mock Exam',
        feature3: 'Core Features',
        currentPlan: 'Current Plan',
        getStarted: 'Get Started',
      },
      socialUnlocked: {
        name: 'Social-Unlocked',
        subtitle: 'Share about the app',
        price: '€0',
        period: 'for 7 days',
        feature1: 'Unlimited Practice Tests',
        feature2: 'Unlimited AI Personalised Exam',
        feature3: 'Everything in Free',
        active: 'Active',
        shareToUnlock: 'Share to Unlock 7 Days Free',
        note: 'After 7 days, your plan returns to Free.',
        socialUnlock: {
          title: 'Unlock Social-Unlocked (7 days)',
          instruction1: 'Share to Instagram or TikTok and tag @theorycoach',
          instruction2: 'Keep post or story visible for 24 hours.',
          shareButton: 'Share',
          note: 'Once you share and tag @theorycoach, premium features are unlocked for 7 days.',
        },
      },
      pro: {
        name: 'Pro',
        subtitle: 'For serious learners',
        mostPopular: 'Most Popular',
        oneMonth: '1 Month',
        threeMonths: '3 Months',
        price1Month: '€15',
        price3Months: '€35',
        period1Month: 'per month',
        period3Months: 'for 3 months',
        save: 'Save €10',
        feature1: 'Unlimited Practice Tests',
        feature2: 'Unlimited AI Personalised Exam',
        feature3: 'Priority Support',
        feature4: 'Everything in Free',
        currentPlan: 'Current Plan',
        upgrade1Month: 'Upgrade for €15/month',
        upgrade3Months: 'Upgrade for €35/3 months',
      },
      footer: {
        text: 'All plans include access to all practice tests and core features',
        note: 'Cancel anytime. No hidden fees.',
      },
      subscribe: 'Subscribe',
      go: {
        name: 'Go',
        subtitle: 'Full access - recurring monthly',
        price: '€15',
        period: 'per month, cancel anytime',
        feature1: 'Unlimited Practice Tests',
        feature2: 'Unlimited AI Personalised Exam',
        feature3: 'Unlimited Access',
        currentPlan: 'Current Plan',
        upgrade: 'Upgrade to Go',
      },
      cookies: {
        title: 'We use cookies',
        description: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
        customize: 'Customize',
        rejectAll: 'Reject All',
        acceptAll: 'Accept All',
        preferencesTitle: 'Cookie Preferences',
        preferencesDescription: 'You can choose which cookies to accept. Necessary cookies are required for the site to function.',
        necessary: 'Necessary Cookies',
        necessaryDescription: 'Required for the site to function. Cannot be disabled.',
        analytics: 'Analytics Cookies',
        analyticsDescription: 'Help us understand how visitors interact with our website.',
        marketing: 'Marketing Cookies',
        marketingDescription: 'Used to deliver personalized advertisements.',
        cancel: 'Cancel',
        savePreferences: 'Save Preferences',
      },
    },
    voiceCommands: {
      optionA: 'a',
      optionB: 'b',
      optionC: 'c',
      optionD: 'd',
      first: 'first',
      second: 'second',
      third: 'third',
      fourth: 'fourth',
      one: 'one',
      two: 'two',
      three: 'three',
      four: 'four',
    },
  },
  nl: {
    welcome: 'Welkom',
    loading: 'Laden...',
    error: 'Er is een fout opgetreden',
    success: 'Succes!',
    next: 'Volgende',
    back: 'Terug',
    cancel: 'Annuleren',
    save: 'Opslaan',
    delete: 'Verwijderen',
    edit: 'Bewerken',
    close: 'Sluiten',
    yes: 'Ja',
    no: 'Nee',
    navigation: {
      dashboard: 'Dashboard',
      practice: 'Oefenen',
      mockExam: 'Proefexamen',
      chat: 'Chat',
      settings: 'Instellingen',
      coach: 'Rapport',
    },
    dashboard: {
      title: 'Dashboard',
      welcomeMessage: "Welkom terug!",
      welcomeSubtitle: "Laten we leren leuk maken!",
      startPractice: "Start",
      performanceTracker: "Jouw Weg naar Succes",
      testsCompleted: "Toetsen Gehaald",
      averageScore: "Jouw Super Score",
      categoryPerformance: "Skill-o-Meter",
      focusAreas: "Power-Ups Nodig",
      focusNote: "Oefen deze om te levelen!",
      upgradeToPremium: "Ontgrendel Epic Modus",
      upgradeDescription: "Alle toetsen, alle functies, alle fun!",
      upgradeNow: "Upgrade & Gaan",
      doingAmazing: "Je gaat als een speer! Ga zo door!",
      makingProgress: "Elke klik is een overwinning!",
      readyToStart: "Avontuur wacht!",
      woohoo: "Woohoo! Je bent een ster!",
      youGotThis: "Je kunt dit! Smashen!",
      firstWin: "Eerste overwinning!",
      examReadiness: "Examen Gereedheid",
      yourJourney: "Jouw Reis",
      studyTime: "Gestudeerd",
      timeRemaining: "Resterend",
      yourProgress: "Blijf Leren",
      gamification: {
        streak: "{days} dagen streak",
        streakAmazing: "{days} dagen streak - Geweldig!",
        level: "Niveau {level} - {kp} KP",
        levelProgress: "Niveau {level} ({progress}% naar volgende)",
        todayProgress: "{progress} vragen vandaag",
        startStreak: "Start vandaag je streak!",
        firstGoal: "Voltooi 1 oefentoets",
        takeTestsToIdentify: "Doe tests om focusgebieden te identificeren",
        almostDone: "{progress} - Bijna klaar!",
        levelBeginner: "Niveau {level} - Beginner"
      },
      todayFocus: "Focus van Vandaag",
      focusOnThis: "Focus op dit gebied om je algehele gereedheid te verbeteren",
      startMockExam: "Start Proefexamen",
      readyToTest: "Klaar om je kennis te testen!",
      notPracticedYet: "Nog niet geoefend - goede volgende stap",
      readyToStartLearning: "Klaar om te beginnen met leren",
      startWithBasics: "Begin met de basis en bouw op!",
      practiceMore: "Meer Oefenen",
      continueBuilding: "Blijf je fundament bouwen",
      excellentMastery: "uitstekende beheersing",
      strongPerformance: "sterke prestatie",
      goodProgress: "goede vooruitgang",
      buildingSkills: "vaardigheden opbouwen",
      loadingProgress: "Jouw voortgang laden...",
      analyzingData: "Jouw studiedata analyseren en inzichten genereren",
      recommendation: "Aanbeveling",
      mockExamsAvailable: "Proefexamens Beschikbaar",
      allTestsCompleted: "Alle oefentoetsen voltooid! Je kunt nu proefexamens maken.",
      startFirstTest: "Begin met je eerste oefentoets!",
      focus: "Focus",
      strength: "Sterkte",
      noWeakAreas: "Nog geen zwakke punten geïdentificeerd. Blijf oefenen!",
      maintainStrength: "Blijf deze sterkte behouden!",
      completeTestsForStrength: "Voltooi oefentoetsen om je sterke punten te zien.",
      aiAdaptive: "AI Adaptief",
      weakAreaFocus: "Zwakke Punt Focus",
      aiPersonalized: "AI Gepersonaliseerd",
      goalAchieved: "Doel Bereikt!",
      goalProgress: "voltooid - {time} resterend",
      aiIdentifiedWeakArea: "AI heeft dit geïdentificeerd als je zwakste gebied ({score}% score)",
      aiRecommendsFocus: "AI raadt aan hier eerst op te focussen",
      currentScore: "Huidige Score",
      targetScore: "Doel: 80%",
      toGo: "{percent}% te gaan",
      personalizedExamInfo: "Je proefexamens worden automatisch gepersonaliseerd op basis van je zwakke punten. Elk examen richt zich op onderwerpen waar je de meeste oefening nodig hebt, waardoor je leerefficiëntie wordt gemaximaliseerd.",
      examFocusedOn: "Gericht op je zwakke punten:",
      examTailored: "Dit examen is afgestemd om je te helpen verbeteren waar je het meest nodig hebt",
      recommendedNext: "Aanbevolen Volgende",
      memoryContext: {
        struggledToday: "Vandaag moeite gehad ({score}%)",
        struggledYesterday: "Gisteren moeite gehad ({score}%)",
        struggledDaysAgo: "{days} dagen geleden moeite gehad ({score}%)"
      }
    },
    settings: {
      title: 'Instellingen',
      subscription: 'Abonnementen',
      account: 'Account',
      language: 'Taal',
      privacy: 'Privacy',
      terms: 'Voorwaarden',
      faq: 'FAQ',
      support: 'Ondersteuning',
      feedback: 'Feedback',
      about: 'Over',
      enterUsername: 'Voer gebruikersnaam in',
      supportSubject: 'Waar heb je hulp bij nodig?',
      supportMessage: 'Beschrijf je probleem of vraag...',
      faqData: {
        progressTracking: {
          question: 'Hoe werkt de voortgangsregistratie?',
          answer: 'Je voortgang wordt automatisch opgeslagen terwijl je oefentoetsen voltooit. Je kunt gedetailleerde analyses bekijken inclusief je gemiddelde score, zwakke punten en studiestreak. Data wordt gesynchroniseerd op al je apparaten wanneer je een account aanmaakt.'
        },
        dataSecurity: {
          question: 'Is mijn data veilig en privé?',
          answer: 'Ja! Je data wordt veilig opgeslagen in de cloud en is alleen toegankelijk voor jou. We delen je persoonlijke informatie niet met derden. Je kunt je account en alle data op elk moment verwijderen.'
        },
        offlineUse: {
          question: 'Kan ik de app offline gebruiken?',
          answer: 'Ja, je kunt de app offline gebruiken voor oefentoetsen. Je voortgang wordt lokaal opgeslagen en gesynchroniseerd naar de cloud wanneer je weer online bent. Sommige functies zoals cloud sync vereisen echter een internetverbinding.'
        },
        aiCoach: {
          question: 'Hoe accuraat is de AI Leercoach?',
          answer: 'De AI Leercoach analyseert je prestaties om gepersonaliseerde studierichtlijnen te geven. Het identificeert je zwakke punten en volgt je leerproces om je effectiever te laten studeren.'
        },
        dataLoss: {
          question: 'Wat als ik mijn apparaat verlies of mijn browser leegmaak?',
          answer: 'Als je een account hebt aangemaakt, wordt je voortgang veilig opgeslagen in de cloud en kan worden hersteld op elk apparaat. Log gewoon in met je naam en al je data is beschikbaar. Voor anonieme gebruikers wordt data lokaal opgeslagen en kan verloren gaan als de browser wordt geleegd.'
        }
      }
    },
    results: {
      title: 'Testresultaten',
      congratulations: "Je hebt het gedaan!",
      wellDone: "Geweldig werk!",
      keepPracticing: "Blijf doorgaan, kampioen!",
      score: 'Score',
      correct: 'Correct',
      incorrect: 'Incorrect',
      totalQuestions: 'Totaal Vragen',
      percentage: 'Percentage',
      timeSpent: 'Tijd Besteed',
      backToPractice: 'Terug naar Oefenen',
      tryAgain: "Probeer het opnieuw!",
      shareResults: "Deel met je vrienden! 🥳",
      testComplete: 'Test Voltooid! Je scoorde {score}/{total} ({percentage}%)',
      excellent: 'Uitstekend werk! Je beheerst dit onderwerp!',
      good: 'Goed gedaan! Blijf oefenen om verder te verbeteren.',
      keepPracticingMessage: 'Blijf oefenen! Je maakt vooruitgang.',
      whatWouldYouLike: 'Wat wil je doen?',
      continueNext: 'Ga door naar Volgende Test',
      retryTest: 'Herhaal Deze Test',
      understandOptions: 'Ik begrijp het. Wil je doorgaan naar de volgende test, deze test opnieuw proberen, of naar het dashboard gaan?',
      typeChoice: 'Typ je antwoord'
    },
    help: {
      needHint: 'Een hint nodig?',
      showExample: 'Toon voorbeeldscenario',
      relatedTopics: 'Gerelateerde onderwerpen die je misschien wilt bekijken:'
    },
    progress: {
      overallProgress: 'Algemene Voortgang',
      milestones: 'Mijlpalen',
      topicStrength: 'Onderwerp Sterkte'
    },
    voice: {
      listening: 'Luisteren...',
      confidence: 'Vertrouwen',
      editMessage: 'Bewerk je bericht...',
      send: 'Verzenden'
    },
    mockExam: {
      mockExamName: 'Standaard Examen',
      mockExamDescription: 'Vaste structuur, spiegelt echt examen weer.',
      personalisedExamName: 'AI Gepersonaliseerd Examen',
      personalisedExamDescription: 'Gepersonaliseerd, spiegelt echt examen weer, richt zich op zwakkere gebieden.',
      aiPersonalizedTitle: 'AI Gepersonaliseerde Proefexamens',
      aiPersonalizedDescription: 'Uw proefexamens worden automatisch gepersonaliseerd op basis van uw zwakke punten. Elk examen richt zich op onderwerpen waar u de meeste oefening nodig heeft, waardoor de leer efficiëntie wordt gemaximaliseerd.',
      aiPersonalized: 'AI Gepersonaliseerd',
      upgradeRealMock: 'Upgrade naar Premium voor onbeperkte Standaard Examens',
      upgradeAI: 'Upgrade naar Premium voor onbeperkte AI Gepersonaliseerde Examens',
      unlockedTitle: 'Proefexamens Ontgrendeld!',
      unlockedMessage: 'Gefeliciteerd! U heeft genoeg oefentoetsen met goede scores voltooid om proefexamens te ontgrendelen.',
      testsCompleted: 'toetsen voltooid',
      averageScore: 'gemiddelde score',
      allTestsAbove70: 'Alle toetsen boven 70%',
      studyTime: 'studietijd',
      startMockExams: 'Start Proefexamens',
      aiExamScore: 'AI Examen Score',
      latestScore: 'Je laatste examenscore',
      noScoreYet: 'Voltooi examen om je score te zien',
      aiExamExplanation1: 'Gepersonaliseerd examen gericht op je zwakke punten',
      aiExamExplanation2: 'Gebruikt echte officiële vragen met adaptieve moeilijkheidsgraad',
      aiExamExplanation3: 'Onbeperkte unieke pogingen - elk examen is anders'
    },
    coach: {
      back: 'Terug',
      reportTitle: 'Coach Rapport',
      readiness: 'Gereedheid',
      accuracy: 'Nauwkeurigheid',
      coverage: 'Dekking',
      consistency: 'Consistentie',
      trend: 'Trend',
      strengths: 'STERKTES',
      improvements: 'VERBETERINGEN',
      recommendations: 'AANBEVELINGEN',
      tests: 'tests',
      gap: 'Gap',
      impact: 'Impact',
      action: 'Actie',
      expected: 'Verwacht',
      priority: 'Prioriteit',
      notAvailable: 'Coach Rapport Niet Beschikbaar',
      requirements: 'Voltooi alle oefentests en het AI Examen om je uitgebreide Coach Rapport te ontgrendelen.',
      practiceTests: 'Oefentests',
      aiExam: 'AI Examen',
      completed: 'Voltooid',
      notCompleted: 'Niet Voltooid',
      completePractice: 'Voltooi Oefentests',
      startAIExam: 'Start AI Examen',
      yourProgress: 'Je Voortgang',
      testsCompleted: 'Tests Voltooid',
      averageScore: 'Gemiddelde Score',
      readinessScore: 'Gereedheid Score',
      examConfidence: 'Examen Vertrouwen'
    },
    achievements: {
      firstTest: {
        title: "Eerste Stappen!",
        description: "Je eerste oefentoets voltooid"
      },
      oneHour: {
        title: "Toegewijde Leerling!",
        description: "1 uur gestudeerd"
      },
      fiveHours: {
        title: "Studie Kampioen!",
        description: "5 uur gestudeerd"
      },
      seventyPercent: {
        title: "Voldoende!",
        description: "70% gemiddelde score behaald"
      },
      eightyFivePercent: {
        title: "Examen Klaar!",
        description: "85% gemiddelde score behaald"
      },
      firstMock: {
        title: "Proefexamen Meester!",
        description: "Je eerste proefexamen voltooid"
      },
      hundredQuestions: {
        title: "Vraag Meester!",
        description: "100 vragen beantwoord"
      },
      fiveHundredQuestions: {
        title: "Theorie Expert!",
        description: "500 vragen beantwoord"
      }
    },
    testNames: {
      trafficLightsSignals: "Verkeerslichten & Signalen",
      priorityRules: "Voorrang & Weggebruik",
      hazardPerception: "Gevaarherkenning",
      speedSafety: "Snelheid & Veiligheid",
      bicycleInteractions: "Fiets Interacties",
      roundaboutRules: "Rotonde Regels",
      tramInteractions: "Tram Interacties",
      pedestrianCrossings: "Voetgangersoversteekplaatsen",
      constructionZones: "Bouwzones",
      weatherConditions: "Weersomstandigheden",
      roadSigns: "Wegborden",
      mandatorySigns: "Gebodsborden",
      prohibitorySigns: "Verbodsborden",
      prohibitorySigns2: "Verbodsborden 2",
      warningSigns: "Waarschuwingsborden",
      roadInformation: "Weginformatie",
      motorwayRules: "Autosnelweg Regels",
      vehicleKnowledge: "Voertuigkennis",
      parkingRules: "Parkeerregels",
      environmental: "Milieuzones",
      technologySafety: "Technologie & Veiligheid",
      adasSafety: "ADAS",
      alcoholDrugs: "Alcohol & Drugs",
      fatigueRest: "Vermoeidheid & Rust",
      emergencyProcedures: "Noodprocedures",
      insightPractice: "Inzicht Oefening",
      mockTest: "Proefexamen",
      roadMarkings: "Wegmarkeringen",
      trafficSigns: "Verkeersborden",
      vehicleHandling: "Voertuigbehandeling",
      riskAwareness: "Risicobewustzijn",
      nightVisibility: "Nachtzichtbaarheid",
      incidentFirstAid: "Incident & EHBO",
      vehicleCategories: "Voertuigcategorieën",
      environmentalZones: "Milieuzones",
      vehicleDocumentation: "Voertuigdocumentatie",
      specializedDriving: "Gespecialiseerd Rijden",
      motorwayMerging: "Invoeging Snelweg"
    },
    practice: {
      title: 'Oefentoetsen',
      startTest: 'Start',
      recommendedForYou: 'Aanbevolen voor Jou',
      hazardPerception: 'Gevaarherkenning',
      insightPractice: 'Inzicht Oefening',
      mandatorySigns: 'Gebodsborden',
      warningSigns: 'Waarschuwingsborden',
      prohibitorySigns: 'Verbodsborden',
      prohibitorySigns2: 'Verbodsborden 2',
      trafficLightsSignals: 'Verkeerslichten & Signalen',
      roadInformation: 'Weginformatie',
      signIdentification: 'Bordherkenning',
      priorityRules: 'Voorrangsregels',
      mandatorySigns2: 'Gebodsborden 2',
      premium: 'Premium',
      question: 'Vraag',
      of: 'van',
      timeLeft: 's',
      nextQuestion: 'Volgende',
      nextTest: 'Volgende Test',
      retake: 'Opnieuw',
      finishTest: 'Voltooien',
      explanation: 'Uitleg:',
      listening: 'Luisteren...',
      sayABCD: 'Selecteer A, B, C of D',
      voiceMode: 'Antwoord Modus',
      mute: 'Dempen',
      unmute: 'Dempen Uitschakelen',
      mockExam: 'Exam',
      chatPlaceholder: 'Vraag over Nederlandse verkeerstheorie of deze vraag...',
      sendMessage: 'Bericht verzenden',
      askAfterExplanation: 'Vraag na de uitleg',
      typeYourQuestion: 'Typ je vraag...',
      continueToNextQuestion: 'Ga door naar volgende vraag',
      proceedToNextQuestion: 'Goede vragen! Klaar om verder te gaan? Laten we doorgaan naar de volgende vraag.',
      coachThinking: 'Coach denkt na...',
      languageHelper: 'Je kunt vragen in het Nederlands, Engels of Arabisch',
      followUpSuggestion1: 'Waarom is deze regel belangrijk?',
      followUpSuggestion2: 'Geef me nog een voorbeeld',
      testProgress: 'Test {completed} van {total} voltooid',
      allTestsCompleted: 'Alle tests voltooid! Je bent klaar voor het Proefexamen.',
      readyForMockExam: 'Alle oefentests voltooid! Klaar voor het Proefexamen?',
      startingNextTest: 'Volgende test starten...',
      mockExamButton: 'Proefexamen',
    },
    mockExamBio: {
      title: 'Exam',
      subtitle: 'Test your knowledge under real exam conditions',
      examOverview: 'Examen Overzicht',
      questions: '50 Vragen',
      questionsDesc: 'Uitgebreide dekking van alle onderwerpen',
      timeLimit: '30 Minuten',
      timeLimitDesc: 'Echte examentijdslimiet',
      passRate: '70% Slagingspercentage',
      passRateDesc: '18/25 juiste antwoorden vereist',
      randomQuestions: 'Willekeurige Vragen',
      randomQuestionsDesc: 'Verschillende vragen elke keer',
      topicsCovered: 'Behandelde Onderwerpen',
      trafficLights: 'Verkeerslichten & Signalen',
      roadSigns: 'Verkeersborden (Alle Types)',
      hazardPerception: 'Gevaarherkenning',
      priorityRules: 'Voorrangsregels',
      roadInformation: 'Weginformatie',
      insightPractice: 'Inzicht Oefening',
      instructions: 'Instructies',
      instruction1: 'Lees elke vraag zorgvuldig voordat je antwoordt',
      instruction2: 'Selecteer het beste antwoord uit de gegeven opties',
      instruction3: 'Je kunt antwoorden bekijken en wijzigen voordat je klaar bent',
      instruction4: 'Het examen wordt automatisch ingediend wanneer de tijd op is',
      instruction5: 'Resultaten worden getoond na voltooiing',
      practiceRequirement: 'Oefenvereiste',
      progressComplete: 'Voltooid',
      readyTitle: "Quizheld!",
      readyMessage: "Je bent quiz-klaar! Tijd om te shinen.",
      almostTitle: "Bijna een legende!",
      almostMessage: "Nog een beetje oefenen en je bent niet te stoppen!",
      practiceTitle: "Eerst opwarmen!",
      practiceMessage: "Nog een paar oefenrondes en je rockt het!",
      startMockExam: 'Start',
      continuePracticing: 'Blijf Oefenen',
      startPracticing: 'Start',
      completedTests: 'Voltooide Toetsen:',
      noTestsCompleted: 'Nog geen toetsen voltooid',
      backToDashboard: 'Terug naar Dashboard'
    },
    quiz: {
      title: 'Exam',
      subtitle: 'Nederlandse Rijtheorie Test',
      officialStyle: 'Officiële Stijl Examen',
      questions: 'Vragen',
      timeLimit: 'Tijdslimiet',
      passRate: 'Slagingspercentage',
      testType: 'Test Type',
      computerBased: 'Computer Gebaseerd',
      variesByCategory: 'Verschilt per categorie',
      instructions: 'Examen Instructies',
      instruction1: 'Lees elke vraag zorgvuldig',
      instruction2: 'Selecteer het beste antwoord uit de opties',
      instruction3: 'Je kunt niet terug naar vorige vragen',
      instruction4: 'De quiz wordt automatisch ingediend bij tijdsverloop',
      instruction5: 'Resultaten worden getoond na voltooiing',
      warning: 'Dit is een officiële-stijl quiz. Zorg ervoor dat je klaar bent voordat je begint.',
      startQuiz: 'Start',
      loading: 'Examen laden...',
      timeLeft: 'Tijd over',
      question: 'Vraag',
      finishQuiz: 'Examen Afmaken',
      previous: 'Vorige',
      next: 'Volgende',
      cancel: 'Annuleren',
      finishQuizConfirm: 'Examen Afmaken',
      finishQuizMessage: 'Weet je zeker dat je de quiz wilt afmaken?',
      answeredQuestions: 'Je hebt {answered} van de {total} vragen beantwoord.',
      results: 'Examen Resultaten',
      score: 'Score',
      correct: 'Correct',
      incorrect: 'Incorrect',
      totalQuestions: 'Totaal Vragen',
      percentage: 'Percentage',
      timeSpent: 'Tijd Besteed',
      backToPractice: 'Terug naar Oefenen',
      tryAgain: 'Opnieuw Proberen',
      shareResults: 'Resultaten Delen',
      congratulations: 'Gefeliciteerd!',
      wellDone: 'Goed gedaan!',
      keepPracticing: 'Blijf oefenen!',
      passed: 'Geslaagd',
      failed: 'Gezakt',
      excellent: 'Uitstekende Prestatie!',
      great: 'Geweldig Werk!',
      good: 'Goed Werk!',
      close: 'Bijna Daar!',
      needPractice: 'Meer Oefening Nodig',
      backToQuizzes: 'Terug naar Quizzes',
      requirement: 'Vereiste:',
      requirementText: 'Scoor 70% of hoger op oefentoetsen om proefexamens te ontgrendelen',
      questionsLabel: 'vragen',
      minutesLabel: 'min',
      passMarkLabel: 'om te slagen',
      completePracticeToUnlock: 'Voltooi oefentoetsen om te ontgrendelen',
      examRules: 'Examenregels',
      aiExamIntroTitle: 'Test je kennis',
      aiExamIntroSubtitle: '50 vragen · 30 minuten · 80% om te slagen',
      rule1: 'Het examen bestaat uit 50 vragen en moet binnen 30 minuten worden voltooid (CBR 2025 formaat).',
      rule2: 'Er zijn minimaal 44 goede antwoorden (88%) vereist om te slagen voor het examen.',
      rule3: 'Elke vraag bevat 3 antwoordopties. Selecteer het ene juiste antwoord.',
      rule4: 'Zodra een antwoord is ingediend, kan het niet meer worden gewijzigd. Dit geldt gedurende het hele examen.',
      rule5: 'Het examen wordt afgenomen op een computer en volgt de officiële CBR examenstructuur en tijdsvereisten.',
      rule6: 'De examentimer blijft lopen, ook als de pagina wordt gesloten of vernieuwd. Beheer uw tijd dienovereenkomstig.',
      aiPersonalizedExam: 'AI Gepersonaliseerd Examen',
      focusedOnWeakAreas: 'Gefocust op uw zwakke punten:',
      tailoredToHelp: 'Dit examen is afgestemd om u te helpen verbeteren waar u het meest nodig heeft'
    },
    quizSelection: {
      title: 'Exam',
      subtitle: 'Choose your difficulty level',
      level1: {
        title: 'Beginners Examen',
        subtitle: '',
        description: '',
        passRate: '80% (20/25 correct)'
      },
      level2: {
        title: 'Gevorderde Examen',
        subtitle: '',
        description: '',
        passRate: '80% (20/25 correct)'
      },
      level3: {
        title: 'Expert Examen',
        subtitle: '',
        description: '',
        passRate: '80% (20/25 correct)'
      },
      difficulty: {
        beginner: 'Beginner',
        intermediate: 'Gevorderd',
        advanced: 'Expert'
      },
      questions: 'Vragen',
      timeLimit: 'Tijdslimiet',
      passRate: 'Slagingspercentage',
      startQuiz: 'Start',
      footer: '',
      status: {
        completed: 'Voltooid',
        attempted: 'Geprobeerd',
        available: 'Beschikbaar',
        locked: 'Vergrendeld'
      },
      bestScore: 'Beste Score',
      locked: 'Vergrendeld',
      unlockMessage: 'Voltooi eerst het {level} niveau om deze quiz te ontgrendelen.',
      progress: '{completed}/3 niveaus voltooid'
    },
    chat: {
      title: 'Chat',
      subtitle: 'Heb je een vraag over verkeersregels of examenthema\'s?',
      placeholder: 'Typ hier je vraag...',
      send: 'Versturen',
      emptyState: "Nog geen berichten. Stel je eerste rijvraag!",
      typing: "Even iets leuks bedenken..."
    },
    aiTutor: {
      title: 'Coach',
      welcome: 'Hoi! Ik kan helpen met Nederlandse rijtheorie, examenkosten, verkeersregels en je voortgang. Wat wil je weten?',
      error: 'Sorry, ik heb nu problemen. Probeer het over een momentje opnieuw.',
      actionItems: 'Actiepunten:',
      nextSteps: 'Volgende Stappen:',
      inputPlaceholder: 'Bericht Coach...',
      quickQuestions: {
        title: 'Probeer te vragen:'
      }
    },
    examDate: {
      title: 'Wanneer is je Examen?',
      selectDate: 'Selecteer Datum',
      skip: 'Overslaan voor nu',
      continue: 'Doorgaan',
      continuing: 'Doorgaan...'
    },
    install: {
      title: 'Installeer theorycoach',
      description: 'Leer rijtheorie in 24 uur met ons AI-aangedreven coachingssysteem',
      mobileMessage: 'Installeer op mobiel voor de beste ervaring',
      installNow: 'Installeer Nu',
      maybeLater: 'Misschien Later'
    },
    progressTracker: {
      title: 'Prestaties',
      emptyMessage: 'Nog geen voortgang. Start je eerste toets!',
      testsCompleted: 'Toetsen Voltooid',
      averageScore: 'Gemiddelde Score',
      dayStreak: 'Dagen Op Rij',
      examReady: 'Klaar voor Examen',
      recentPerformance: 'Recente Prestatie',
      focusAreas: 'Focusgebieden',
      strongAreas: 'Sterke Punten',
      noWeakAreas: 'Geen zwakke punten gevonden.',
      noStrongAreas: 'Nog geen sterke punten.',
      practiceWeakAreas: 'Oefen Zwakke Punten',
      takeMockExam: 'Maak Proefexamen',
      improving: 'Verbeterend',
      declining: 'Afnemend',
    },
    testCategories: {
      hazardPerception: 'Gevaarherkenning',
      insightPractice: 'Inzicht Oefening',
      mandatorySigns: 'Gebodsborden',
      warningSigns: 'Waarschuwingsborden',
      prohibitorySigns: 'Verbodsborden',
      prohibitorySigns2: 'Verbodsborden 2',
      trafficLightsSignals: 'Verkeerslichten & Signalen',
      roadInformation: 'Weginformatie',
      signIdentification: 'Bordherkenning',
      priorityRules: 'Voorrangsregels',
      mandatorySigns2: 'Gebodsborden 2',
      mockExam: 'Exam',
    },
    subscription: {
      title: 'Eenvoudige, eerlijke prijzen',
      subtitle: 'Begin gratis. Ontgrendel meer door te delen of ga Pro voor onbeperkte kracht.',
      free: {
        name: 'Gratis',
        subtitle: 'Beste om uit te proberen',
        price: '€0',
        period: 'voor altijd',
        feature1: '10 Oefentoetsen',
        feature2: '1 Standaard Proefexamen',
        feature3: 'Kernfuncties',
        currentPlan: 'Huidig Plan',
        getStarted: 'Aan de slag',
      },
      socialUnlocked: {
        name: 'Sociaal-Ontgrendeld',
        subtitle: 'Deel over de app',
        price: '€0',
        period: 'voor 7 dagen',
        feature1: 'Onbeperkte Oefentoetsen',
        feature2: 'Onbeperkte AI Gepersonaliseerd Examen',
        feature3: 'Alles in Gratis',
        active: 'Actief',
        shareToUnlock: 'Deel om 7 Dagen Gratis te Ontgrendelen',
        note: 'Na 7 dagen keert uw plan terug naar Gratis.',
        socialUnlock: {
          title: 'Ontgrendel Sociaal-Ontgrendeld (7 dagen)',
          instruction1: 'Deel op Instagram of TikTok en tag @theorycoach',
          instruction2: 'Houd post of story 24 uur zichtbaar.',
          shareButton: 'Delen',
          note: 'Zodra je deelt en @theorycoach tagt, worden premium functies 7 dagen ontgrendeld.',
        },
      },
      pro: {
        name: 'Pro',
        subtitle: 'Voor serieuze leerlingen',
        mostPopular: 'Meest Populair',
        oneMonth: '1 Maand',
        threeMonths: '3 Maanden',
        price1Month: '€15',
        price3Months: '€35',
        period1Month: 'per maand',
        period3Months: 'voor 3 maanden',
        save: 'Bespaar €10',
        feature1: 'Onbeperkte Oefentoetsen',
        feature2: 'Onbeperkte AI Gepersonaliseerd Examen',
        feature3: 'Prioriteitsondersteuning',
        feature4: 'Alles in Gratis',
        currentPlan: 'Huidig Plan',
        upgrade1Month: 'Upgrade naar Pro voor €15/maand',
        upgrade3Months: 'Upgrade naar Pro voor €35/3 maanden',
      },
      footer: {
        text: 'Alle plannen bevatten toegang tot alle oefentoetsen en kernfuncties',
        note: 'Annuleer op elk moment. Geen verborgen kosten.',
      },
      subscribe: 'Abonneren',
      go: {
        name: 'Go',
        subtitle: 'Volledige toegang - maandelijks terugkerend',
        price: '€15',
        period: 'per maand, op elk moment opzegbaar',
        feature1: 'Onbeperkte Oefentoetsen',
        feature2: 'Onbeperkt AI Gepersonaliseerd Examen',
        feature3: 'Onbeperkte Toegang',
        currentPlan: 'Huidig Plan',
        upgrade: 'Upgrade naar Go',
      },
      cookies: {
        title: 'We gebruiken cookies',
        description: 'We gebruiken cookies om uw browse-ervaring te verbeteren, gepersonaliseerde inhoud te leveren en ons verkeer te analyseren. Door op "Alles accepteren" te klikken, stemt u in met ons gebruik van cookies.',
        customize: 'Aanpassen',
        rejectAll: 'Alles weigeren',
        acceptAll: 'Alles accepteren',
        preferencesTitle: 'Cookie-voorkeuren',
        preferencesDescription: 'U kunt kiezen welke cookies u accepteert. Noodzakelijke cookies zijn vereist voor de werking van de site.',
        necessary: 'Noodzakelijke cookies',
        necessaryDescription: 'Vereist voor de werking van de site. Kan niet worden uitgeschakeld.',
        analytics: 'Analytische cookies',
        analyticsDescription: 'Helpen ons te begrijpen hoe bezoekers met onze website interacteren.',
        marketing: 'Marketing cookies',
        marketingDescription: 'Gebruikt om gepersonaliseerde advertenties te leveren.',
        cancel: 'Annuleren',
        savePreferences: 'Voorkeuren opslaan',
      },
    },
    voiceCommands: {
      optionA: 'a',
      optionB: 'b',
      optionC: 'c',
      optionD: 'd',
      first: 'eerste',
      second: 'tweede',
      third: 'derde',
      fourth: 'vierde',
      one: 'een',
      two: 'twee',
      three: 'drie',
      four: 'vier',
    },
    streak: {
      warningTitle: 'Je streak loopt gevaar!',
      warningMessage: 'Je hebt {minutes} minuten over om je {days}-daagse streak in stand te houden!',
      freezeStreak: 'Streak Bevriezen',
      protected: 'Streak beschermd — ga vandaag door 🔥',
      signup: 'Aanmelden',
      signupMessage: 'Meld je aan om je streak te starten 🔥'
    },
    leaderboard: {
      title: 'Ranglijst',
      weekly: 'Wekelijks',
      city: 'Stad',
      friends: 'Vrienden',
      yourRank: 'Jouw rang'
    },
    common: {
      send: 'Verzenden'
    }
  },
  ar: {
    welcome: 'مرحباً',
    loading: 'جاري التحميل...',
    error: 'حدث خطأ',
    success: 'نجح!',
    next: 'التالي',
    back: 'رجوع',
    cancel: 'إلغاء',
    save: 'حفظ',
    delete: 'حذف',
    edit: 'تعديل',
    close: 'إغلاق',
    yes: 'نعم',
    no: 'لا',
    navigation: {
      dashboard: 'لوحة التحكم',
      practice: 'التدريب',
      mockExam: 'الامتحان التجريبي',
      chat: 'المحادثة',
      settings: 'الإعدادات',
      coach: 'التقرير',
    },
    dashboard: {
      title: 'لوحة التحكم',
      welcomeMessage: "مرحباً {username}، جاهز للتألق اليوم؟",
      welcomeSubtitle: "لنستمتع بالتعلم!",
      startPractice: "ابدأ",
      performanceTracker: "طريقك إلى النجاح",
      testsCompleted: "الاختبارات المنجزة",
      averageScore: "درجتك المميزة",
      categoryPerformance: "عداد المهارات",
      focusAreas: "مناطق تحتاج تعزيز",
      focusNote: "تدرب هنا لترتقي!",
      upgradeToPremium: "افتح الوضع الأسطوري",
      upgradeDescription: "كل الاختبارات، كل الميزات، كل المتعة!",
      upgradeNow: "ترقية وانطلاق",
      doingAmazing: "أنت تتقدم بسرعة! استمر!",
      makingProgress: "كل نقرة انتصار!",
      readyToStart: "المغامرة بانتظارك!",
      woohoo: "واو! أنت نجم!",
      youGotThis: "أنت تستطيع! اكسرها!",
      firstWin: "أول فوز!",
      examReadiness: "جاهزية الامتحان",
      yourJourney: "رحلتك",
      studyTime: "مُدَرَّس",
      timeRemaining: "متبقي",
      yourProgress: "استمر في التعلم",
      gamification: {
        streak: "سلسلة {days} أيام",
        streakAmazing: "سلسلة {days} أيام - رائع!",
        level: "المستوى {level} - {kp} نقطة",
        levelProgress: "المستوى {level} ({progress}% إلى التالي)",
        todayProgress: "{progress} أسئلة اليوم",
        startStreak: "ابدأ سلسلتك اليوم!",
        firstGoal: "أكمل اختبار تدريبي واحد",
        takeTestsToIdentify: "قم بإجراء الاختبارات لتحديد مجالات التركيز",
        almostDone: "{progress} - تقريباً انتهى!",
        levelBeginner: "المستوى {level} - مبتدئ"
      },
      todayFocus: "التركيز اليوم",
      focusOnThis: "ركز على هذا المجال لتحسين جاهزيتك العامة",
      startMockExam: "ابدأ الامتحان التجريبي",
      readyToTest: "جاهز لاختبار معرفتك!",
      notPracticedYet: "لم يتم التدرب بعد - خطوة جيدة التالية",
      readyToStartLearning: "جاهز لبدء التعلم",
      startWithBasics: "ابدأ بالأساسيات وابنِ عليها!",
      practiceMore: "تدرب أكثر",
      continueBuilding: "استمر في بناء أساسك",
      excellentMastery: "إتقان ممتاز",
      strongPerformance: "أداء قوي",
      goodProgress: "تقدم جيد",
      buildingSkills: "بناء المهارات",
      loadingProgress: "تحميل تقدمك...",
      analyzingData: "تحليل بيانات دراستك وتوليد رؤى",
      recommendation: "توصية",
      mockExamsAvailable: "الامتحانات التجريبية متاحة",
      allTestsCompleted: "اكتملت جميع اختبارات التدريب! يمكنك الآن إجراء الامتحانات التجريبية.",
      startFirstTest: "ابدأ باختبار التدريب الأول!",
      focus: "التركيز",
      strength: "القوة",
      noWeakAreas: "لم يتم تحديد نقاط ضعف بعد. استمر في التدريب!",
      maintainStrength: "استمر في الحفاظ على هذه القوة!",
      completeTestsForStrength: "أكمل اختبارات التدريب لرؤية نقاط قوتك.",
      aiAdaptive: "ذكي تكيفي",
      weakAreaFocus: "تركيز على نقاط الضعف",
      aiPersonalized: "مخصص بالذكاء الاصطناعي",
      goalAchieved: "تم تحقيق الهدف!",
      goalProgress: "مكتمل - {time} متبقي",
      aiIdentifiedWeakArea: "حدد الذكاء الاصطناعي هذا كأضعف منطقة لديك ({score}% درجة)",
      aiRecommendsFocus: "يوصي الذكاء الاصطناعي بالتركيز هنا أولاً",
      currentScore: "الدرجة الحالية",
      targetScore: "الهدف: 80%",
      toGo: "{percent}% متبقي",
      personalizedExamInfo: "يتم تخصيص امتحاناتك التجريبية تلقائياً بناءً على نقاط ضعفك. يركز كل امتحان على المواضيع التي تحتاج فيها إلى أكبر قدر من التدريب، مما يضمن أقصى كفاءة تعليمية.",
      examFocusedOn: "مركز على نقاط ضعفك:",
      examTailored: "تم تصميم هذا الامتحان لمساعدتك على التحسين حيث تحتاج إليه أكثر",
      recommendedNext: "الموصى به التالي",
      memoryContext: {
        struggledToday: "واجهت صعوبة اليوم ({score}%)",
        struggledYesterday: "واجهت صعوبة أمس ({score}%)",
        struggledDaysAgo: "واجهت صعوبة منذ {days} أيام ({score}%)"
      }
    },
    settings: {
      title: 'الإعدادات',
      subscription: 'الاشتراكات',
      account: 'الحساب',
      language: 'اللغة',
      privacy: 'الخصوصية',
      terms: 'الشروط',
      faq: 'الأسئلة الشائعة',
      support: 'الدعم',
      feedback: 'ملاحظات',
      about: 'حول',
      enterUsername: 'أدخل اسم المستخدم',
      supportSubject: 'بماذا تحتاج مساعدة؟',
      supportMessage: 'اوصف مشكلتك أو سؤالك...',
      faqData: {
        progressTracking: {
          question: 'كيف يعمل تتبع التقدم؟',
          answer: 'يتم حفظ تقدمك تلقائياً أثناء إكمال اختبارات التدريب. يمكنك عرض تحليلات مفصلة تشمل متوسط درجاتك والمناطق الضعيفة ومسار الدراسة. يتم مزامنة البيانات عبر جميع أجهزتك عند إنشاء حساب.'
        },
        dataSecurity: {
          question: 'هل بياناتي آمنة وخاصة؟',
          answer: 'نعم! يتم تخزين بياناتك بأمان في السحابة وهي متاحة لك فقط. نحن لا نشارك معلوماتك الشخصية مع أطراف ثالثة. يمكنك حذف حسابك وجميع البيانات في أي وقت.'
        },
        offlineUse: {
          question: 'هل يمكنني استخدام التطبيق بدون اتصال؟',
          answer: 'نعم، يمكنك استخدام التطبيق بدون اتصال لاختبارات التدريب. سيتم حفظ تقدمك محلياً ومزامنته إلى السحابة عندما تعود متصلاً. ومع ذلك، تتطلب بعض الميزات مثل مزامنة السحابة اتصالاً بالإنترنت.'
        },
        aiCoach: {
          question: 'ما مدى دقة مدرب التعلم بالذكاء الاصطناعي؟',
          answer: 'يحلل مدرب التعلم بالذكاء الاصطناعي أنماط أدائك لتقديم توصيات دراسة مخصصة. يحدد المناطق الضعيفة ويتتبع تقدمك في التعلم لمساعدتك على الدراسة بفعالية أكبر.'
        },
        dataLoss: {
          question: 'ماذا لو فقدت جهازي أو مسحت المتصفح؟',
          answer: 'إذا كنت قد أنشأت حساباً، يتم تخزين تقدمك بأمان في السحابة ويمكن استعادته على أي جهاز. ببساطة سجل دخولك باسمك وستكون جميع بياناتك متاحة. للمستخدمين المجهولين، يتم تخزين البيانات محلياً وقد تضيع إذا تم مسح المتصفح.'
        }
      }
    },
    mockExam: {
      mockExamName: 'الامتحان القياسي',
      mockExamDescription: 'هيكل ثابت، يعكس الامتحان الحقيقي.',
      personalisedExamName: 'الامتحان المخصص بالذكاء الاصطناعي',
      personalisedExamDescription: 'مخصص، يعكس الامتحان الحقيقي، يركز على المناطق الأضعف.',
      aiPersonalizedTitle: 'امتحانات تجريبية مخصصة بالذكاء الاصطناعي',
      aiPersonalizedDescription: 'يتم تخصيص امتحاناتك التجريبية تلقائياً بناءً على نقاط ضعفك. يركز كل امتحان على المواضيع التي تحتاج فيها إلى أكبر قدر من الممارسة، مما يضمن أقصى كفاءة في التعلم.',
      aiPersonalized: 'مخصص بالذكاء الاصطناعي',
      upgradeRealMock: 'ترقية إلى Premium للحصول على امتحانات قياسية غير محدودة',
      upgradeAI: 'ترقية إلى Premium للحصول على امتحانات مخصصة بالذكاء الاصطناعي غير محدودة',
      unlockedTitle: 'تم فتح الامتحانات التجريبية!',
      unlockedMessage: 'تهانينا! لقد أكملت ما يكفي من اختبارات الممارسة بدرجات جيدة لفتح الامتحانات التجريبية.',
      testsCompleted: 'اختبارات مكتملة',
      averageScore: 'متوسط النتيجة',
      allTestsAbove70: 'جميع الاختبارات أعلى من 70%',
      studyTime: 'وقت الدراسة',
      startMockExams: 'بدء الامتحانات التجريبية',
      aiExamScore: 'نتيجة امتحان الذكاء الاصطناعي',
      latestScore: 'نتيجتك الأخيرة',
      noScoreYet: 'أكمل الامتحان لرؤية نتيجتك',
      aiExamExplanation1: 'امتحان مخصص يركز على نقاط ضعفك',
      aiExamExplanation2: 'يستخدم أسئلة رسمية حقيقية مع صعوبة تكيفية',
      aiExamExplanation3: 'محاولات غير محدودة وفريدة - كل امتحان مختلف'
    },
    coach: {
      back: 'رجوع',
      reportTitle: 'تقرير المدرب',
      readiness: 'الاستعداد',
      accuracy: 'الدقة',
      coverage: 'التغطية',
      consistency: 'الاتساق',
      trend: 'الاتجاه',
      strengths: 'نقاط القوة',
      improvements: 'مجالات التحسين',
      recommendations: 'التوصيات',
      tests: 'اختبارات',
      gap: 'الفجوة',
      impact: 'التأثير',
      action: 'الإجراء',
      expected: 'المتوقع',
      priority: 'الأولوية',
      notAvailable: 'تقرير المدرب غير متاح',
      requirements: 'أكمل جميع اختبارات التدريب وامتحان الذكاء الاصطناعي لفتح تقرير المدرب الشامل.',
      practiceTests: 'اختبارات التدريب',
      aiExam: 'امتحان الذكاء الاصطناعي',
      completed: 'مكتمل',
      notCompleted: 'غير مكتمل',
      completePractice: 'أكمل اختبارات التدريب',
      startAIExam: 'ابدأ امتحان الذكاء الاصطناعي',
      yourProgress: 'تقدمك',
      testsCompleted: 'الاختبارات المكتملة',
      averageScore: 'متوسط النتيجة',
      readinessScore: 'نتيجة الاستعداد',
      examConfidence: 'ثقة الامتحان'
    },
    achievements: {
      firstTest: {
        title: "الخطوات الأولى!",
        description: "أكملت أول اختبار تدريبي"
      },
      oneHour: {
        title: "متعلم مخلص!",
        description: "درست لمدة ساعة واحدة"
      },
      fiveHours: {
        title: "بطل الدراسة!",
        description: "درست لمدة 5 ساعات"
      },
      seventyPercent: {
        title: "درجة النجاح!",
        description: "حصلت على متوسط 70%"
      },
      eightyFivePercent: {
        title: "جاهز للامتحان!",
        description: "حصلت على متوسط 85%"
      },
      firstMock: {
        title: "سيد الامتحان التجريبي!",
        description: "أكملت أول امتحان تجريبي"
      },
      hundredQuestions: {
        title: "سيد الأسئلة!",
        description: "أجبت على 100 سؤال"
      },
      fiveHundredQuestions: {
        title: "خبير النظرية!",
        description: "أجبت على 500 سؤال"
      }
    },
    testNames: {
      trafficLightsSignals: "إشارات المرور والإشارات",
      priorityRules: "الأولوية وحق الطريق",
      hazardPerception: "إدراك المخاطر",
      speedSafety: "السرعة والسلامة",
      bicycleInteractions: "تفاعلات الدراجات",
      roundaboutRules: "قواعد الدوار",
      tramInteractions: "تفاعلات الترام",
      pedestrianCrossings: "معابر المشاة",
      constructionZones: "مناطق البناء",
      weatherConditions: "الظروف الجوية",
      roadSigns: "علامات الطريق",
      mandatorySigns: "العلامات الإلزامية",
      prohibitorySigns: "علامات المنع",
      prohibitorySigns2: "علامات المنع 2",
      warningSigns: "علامات التحذير",
      roadInformation: "معلومات الطريق",
      motorwayRules: "قواعد الطرق السريعة",
      vehicleKnowledge: "معرفة المركبات",
      parkingRules: "قواعد الوقوف",
      environmental: "المناطق البيئية",
      technologySafety: "التكنولوجيا والسلامة",
      adasSafety: "ADAS",
      alcoholDrugs: "الكحول والمخدرات",
      fatigueRest: "التعب والراحة",
      emergencyProcedures: "إجراءات الطوارئ",
      insightPractice: "تدريب البصيرة",
      mockTest: "اختبار تجريبي",
      roadMarkings: "علامات الطريق الأرضية",
      trafficSigns: "علامات المرور",
      vehicleHandling: "التحكم في المركبة",
      riskAwareness: "الوعي بالمخاطر",
      nightVisibility: "الرؤية الليلية",
      incidentFirstAid: "الحوادث والإسعافات الأولية",
      vehicleCategories: "فئات المركبات",
      environmentalZones: "المناطق البيئية",
      vehicleDocumentation: "وثائق المركبة",
      specializedDriving: "القيادة المتخصصة",
      motorwayMerging: "الاندماج في الطريق السريع"
    },
    practice: {
      title: 'اختبارات التدريب',
      startTest: 'ابدأ',
      recommendedForYou: 'موصى لك',
      hazardPerception: 'إدراك المخاطر',
      insightPractice: 'تدريب البصيرة',
      mandatorySigns: 'العلامات الإلزامية',
      warningSigns: 'علامات التحذير',
      prohibitorySigns: 'علامات الحظر',
      prohibitorySigns2: 'علامات الحظر 2',
      trafficLightsSignals: 'إشارات المرور',
      roadInformation: 'معلومات الطريق',
      signIdentification: 'تحديد العلامات',
      priorityRules: 'قواعد الأولوية',
      mandatorySigns2: 'العلامات الإلزامية 2',
      premium: 'مميز',
      question: 'سؤال',
      of: 'من',
      timeLeft: 'ثانية',
      nextQuestion: 'التالي',
      nextTest: 'الاختبار التالي',
      retake: 'إعادة المحاولة',
      finishTest: 'إنهاء',
      explanation: 'شرح:',
      listening: 'استماع...',
      sayABCD: 'اختر أ، ب، ج، أو د',
      voiceMode: 'وضع الإجابة',
      mute: 'كتم الصوت',
      unmute: 'إلغاء كتم الصوت',
      mockExam: 'اختبار تجريبي',
      chatPlaceholder: 'اسأل عن نظرية القيادة الهولندية أو هذا السؤال...',
      sendMessage: 'إرسال رسالة',
      askAfterExplanation: 'اسأل بعد الشرح',
      typeYourQuestion: 'اكتب سؤالك...',
      continueToNextQuestion: 'المتابعة إلى السؤال التالي',
      proceedToNextQuestion: 'أسئلة رائعة! هل أنت مستعد للمتابعة؟ دعنا ننتقل إلى السؤال التالي.',
      coachThinking: 'المدرب يفكر...',
      languageHelper: 'يمكنك السؤال بالهولندية أو الإنجليزية أو العربية',
      followUpSuggestion1: 'لماذا هذه القاعدة مهمة؟',
      followUpSuggestion2: 'أعطني مثالاً آخر',
      testProgress: 'تم إكمال {completed} من {total} اختبار',
      allTestsCompleted: 'تم إكمال جميع الاختبارات! أنت مستعد لامتحان تجريبي.',
      readyForMockExam: 'تم إكمال جميع اختبارات الممارسة! مستعد للامتحان التجريبي؟',
      startingNextTest: 'بدء الاختبار التالي...',
      mockExamButton: 'امتحان تجريبي',
    },
    mockExamBio: {
      title: 'اختبار نظرية القيادة الهولندية',
      subtitle: 'اختبر معرفتك في ظروف الامتحان الحقيقية',
      examOverview: 'نظرة عامة على الامتحان',
      questions: '50 سؤال',
      questionsDesc: 'تغطية شاملة لجميع المواضيع',
      timeLimit: '30 دقيقة',
      timeLimitDesc: 'الحد الزمني الحقيقي للامتحان',
      passRate: '70% معدل النجاح',
      passRateDesc: 'مطلوب 18/25 إجابة صحيحة',
      randomQuestions: 'أسئلة عشوائية',
      randomQuestionsDesc: 'أسئلة مختلفة في كل مرة',
      topicsCovered: 'المواضيع المغطاة',
      trafficLights: 'إشارات المرور',
      roadSigns: 'علامات الطريق (جميع الأنواع)',
      hazardPerception: 'إدراك المخاطر',
      priorityRules: 'قواعد الأولوية',
      roadInformation: 'معلومات الطريق',
      insightPractice: 'تدريب البصيرة',
      instructions: 'التعليمات',
      instruction1: 'اقرأ كل سؤال بعناية قبل الإجابة',
      instruction2: 'اختر أفضل إجابة من الخيارات المعطاة',
      instruction3: 'يمكنك مراجعة وتغيير الإجابات قبل الإنهاء',
      instruction4: 'سيتم إرسال الامتحان تلقائياً عند انتهاء الوقت',
      instruction5: 'ستظهر النتائج بعد الانتهاء',
      practiceRequirement: 'متطلب التدريب',
      progressComplete: 'مكتمل',
      readyTitle: 'بطل الاختبار!',
      readyMessage: "أنت جاهز للاختبار! أظهر مهاراتك.",
      almostTitle: 'قريب من الأسطورة!',
      almostMessage: "بعض التدريب الإضافي وستصبح لا يُقهر!",
      practiceTitle: 'سخّن أولاً!',
      practiceMessage: "بعض الجولات التدريبية وستتفوق!",
      startMockExam: 'ابدأ',
      continuePracticing: 'استمر في التدريب',
      startPracticing: 'ابدأ',
      completedTests: 'الاختبارات المكتملة:',
      noTestsCompleted: 'لم تكتمل أي اختبارات بعد',
      backToDashboard: 'العودة إلى لوحة التحكم'
    },
    quiz: {
      title: 'اختبار تجريبي',
      subtitle: 'اختبار نظرية القيادة الهولندية',
      officialStyle: 'اختبار بأسلوب رسمي',
      questions: 'أسئلة',
      timeLimit: 'الحد الزمني',
      passRate: 'معدل النجاح',
      testType: 'نوع الاختبار',
      computerBased: 'قائم على الكمبيوتر',
      variesByCategory: 'يختلف حسب الفئة',
      instructions: 'تعليمات الاختبار',
      instruction1: 'اقرأ كل سؤال بعناية',
      instruction2: 'اختر أفضل إجابة من الخيارات',
      instruction3: 'لا يمكنك العودة إلى الأسئلة السابقة',
      instruction4: 'سيتم إرسال الاختبار تلقائياً عند انتهاء الوقت',
      instruction5: 'ستظهر النتائج بعد الانتهاء',
      warning: 'هذا اختبار بأسلوب رسمي. تأكد من أنك جاهز قبل البدء.',
      startQuiz: 'ابدأ',
      loading: 'تحميل الاختبار...',
      timeLeft: 'الوقت المتبقي',
      question: 'سؤال',
      finishQuiz: 'إنهاء الاختبار',
      previous: 'السابق',
      next: 'التالي',
      cancel: 'إلغاء',
      finishQuizConfirm: 'إنهاء الاختبار',
      finishQuizMessage: 'هل أنت متأكد من أنك تريد إنهاء الاختبار؟',
      answeredQuestions: 'لقد أجبت على {answered} من أصل {total} سؤال.',
      results: 'نتائج الاختبار',
      score: 'النتيجة',
      correct: 'صحيح',
      incorrect: 'خطأ',
      totalQuestions: 'إجمالي الأسئلة',
      percentage: 'النسبة المئوية',
      timeSpent: 'الوقت المستغرق',
      backToPractice: 'العودة إلى التدريب',
      tryAgain: 'حاول مرة أخرى',
      shareResults: 'شارك النتائج',
      congratulations: 'تهانينا!',
      wellDone: 'أحسنت!',
      keepPracticing: 'استمر في التدريب!',
      passed: 'نجح',
      failed: 'فشل',
      excellent: 'أداء ممتاز!',
      great: 'عمل رائع!',
      good: 'عمل جيد!',
      close: 'تقريباً هناك!',
      needPractice: 'تحتاج مزيد من التدريب',
      backToQuizzes: 'العودة إلى الاختبارات',
      requirement: 'المتطلب:',
      requirementText: 'احصل على 70% أو أعلى في اختبارات التدريب لفتح الامتحانات التجريبية',
      questionsLabel: 'أسئلة',
      minutesLabel: 'دقيقة',
      passMarkLabel: 'للنجاح',
      completePracticeToUnlock: 'أكمل اختبارات التدريب لفتح القفل',
      examRules: 'قواعد الامتحان',
      aiExamIntroTitle: 'اختبر معرفتك',
      aiExamIntroSubtitle: '50 سؤالاً · 30 دقيقة · 80% للنجاح',
      rule1: 'يتكون الامتحان من 50 سؤالاً ويجب إكماله خلال 30 دقيقة (تنسيق CBR 2025).',
      rule2: 'يتطلب النجاح في الامتحان ما لا يقل عن 44 إجابة صحيحة (88%).',
      rule3: 'يحتوي كل سؤال على 3 خيارات إجابة. اختر الإجابة الصحيحة الواحدة.',
      rule4: 'بمجرد تقديم إجابة، لا يمكن تغييرها. ينطبق هذا طوال فترة الامتحان.',
      rule5: 'يتم إجراء الامتحان على الكمبيوتر ويتبع الهيكل والتوقيت الرسمي لـ CBR.',
      rule6: 'يستمر مؤقت الامتحان في العمل حتى إذا تم إغلاق الصفحة أو تحديثها. قم بإدارة وقتك وفقاً لذلك.',
      aiPersonalizedExam: 'امتحان ذكي مخصص',
      focusedOnWeakAreas: 'يركز على نقاط ضعفك:',
      tailoredToHelp: 'تم تصميم هذا الامتحان لمساعدتك على التحسين حيث تحتاج إليه أكثر'
    },
    quizSelection: {
      title: 'اختبار',
      subtitle: 'اختر مستوى الصعوبة',
      level1: {
        title: 'اختبار المبتدئين',
        subtitle: '',
        description: '',
        passRate: '80% (20/25 صحيح)'
      },
      level2: {
        title: 'اختبار المتوسط',
        subtitle: '',
        description: '',
        passRate: '80% (20/25 صحيح)'
      },
      level3: {
        title: 'اختبار المتقدم',
        subtitle: '',
        description: '',
        passRate: '80% (20/25 صحيح)'
      },
      difficulty: {
        beginner: 'مبتدئ',
        intermediate: 'متوسط',
        advanced: 'متقدم'
      },
      questions: 'أسئلة',
      timeLimit: 'الحد الزمني',
      passRate: 'معدل النجاح',
      startQuiz: 'ابدأ',
      footer: '',
      status: {
        completed: 'مكتمل',
        attempted: 'محاولة',
        available: 'متاح',
        locked: 'مقفل'
      },
      bestScore: 'أفضل نتيجة',
      locked: 'مقفل',
      unlockMessage: 'أكمل مستوى {level} أولاً لفتح هذا الاختبار.',
      progress: '{completed}/3 مستويات مكتملة'
    },
    chat: {
      title: 'المحادثة',
      subtitle: 'هل لديك سؤال حول قواعد الطريق أو مواضيع الامتحان؟',
      placeholder: 'اكتب سؤالك هنا...',
      send: 'إرسال',
      emptyState: "اسألني أي شيء! أنا مساعدك في القيادة.",
      typing: "أفكر في إجابة رائعة..."
    },
    results: {
      title: 'نتائج الاختبار',
      congratulations: "لقد فعلتها!",
      wellDone: "عمل رائع!",
      keepPracticing: "استمر أيها البطل!",
      score: 'النتيجة',
      correct: 'صحيح',
      incorrect: 'خطأ',
      totalQuestions: 'إجمالي الأسئلة',
      percentage: 'النسبة المئوية',
      timeSpent: 'الوقت المستغرق',
      backToPractice: 'العودة إلى التدريب',
      tryAgain: "جرب مرة أخرى!",
      shareResults: "شارك أصدقاءك! 🥳",
      testComplete: 'اكتمل الاختبار! لقد حصلت على {score}/{total} ({percentage}%)',
      excellent: 'عمل ممتاز! أنت تتقن هذا الموضوع!',
      good: 'عمل جيد! استمر في التدريب للتحسين أكثر.',
      keepPracticingMessage: 'استمر في التدريب! أنت تحرز تقدماً.',
      whatWouldYouLike: 'ماذا تريد أن تفعل؟',
      continueNext: 'المتابعة إلى الاختبار التالي',
      retryTest: 'إعادة هذا الاختبار',
      understandOptions: 'أفهم. هل تريد المتابعة إلى الاختبار التالي، إعادة هذا الاختبار، أو الذهاب إلى لوحة التحكم؟',
      typeChoice: 'اكتب إجابتك'
    },
    help: {
      needHint: 'تحتاج إلى تلميح؟',
      showExample: 'عرض سيناريو مثال',
      relatedTopics: 'مواضيع ذات صلة قد ترغب في مراجعتها:'
    },
    progress: {
      overallProgress: 'التقدم العام',
      milestones: 'المعالم',
      topicStrength: 'قوة الموضوع'
    },
    voice: {
      listening: 'جاري الاستماع...',
      confidence: 'الثقة',
      editMessage: 'تحرير رسالتك...',
      send: 'إرسال'
    },
    streak: {
      warningTitle: 'سلسلتك في خطر!',
      warningMessage: 'لديك {minutes} دقيقة متبقية للحفاظ على سلسلتك لمدة {days} أيام!',
      freezeStreak: 'تجميد السلسلة',
      protected: 'السلسلة محمية — استمر اليوم 🔥',
      signup: 'سجل',
      signupMessage: 'سجل لبدء سلسلتك 🔥'
    },
    leaderboard: {
      title: 'لوحة المتصدرين',
      weekly: 'أسبوعي',
      city: 'المدينة',
      friends: 'الأصدقاء',
      yourRank: 'رتبتك'
    },
    common: {
      send: 'إرسال'
    },
    aiTutor: {
      title: 'المدرب',
      welcome: 'مرحباً! يمكنني المساعدة في نظرية القيادة الهولندية وتكاليف الامتحان وقواعد المرور وتقدمك. ماذا تريد أن تعرف؟',
      error: 'عذراً، أواجه مشاكل الآن. يرجى المحاولة مرة أخرى بعد لحظة.',
      actionItems: 'نقاط العمل:',
      nextSteps: 'الخطوات التالية:',
      inputPlaceholder: 'رسالة للمدرب...',
      quickQuestions: {
        title: 'جرب السؤال عن:'
      }
    },
    examDate: {
      title: 'متى امتحانك؟',
      selectDate: 'اختر التاريخ',
      skip: 'تخطي الآن',
      continue: 'متابعة',
      continuing: 'جاري المتابعة...'
    },
    install: {
      title: 'تثبيت مدرب النظرية الذكي',
      description: 'تعلم نظرية القيادة في 24 ساعة مع نظام التدريب المدعوم بالذكاء الاصطناعي',
      mobileMessage: 'ثبت على الهاتف المحمول للحصول على أفضل تجربة',
      installNow: 'تثبيت الآن',
      maybeLater: 'ربما لاحقاً'
    },
    progressTracker: {
      title: 'الأداء',
      emptyMessage: 'لا يوجد تقدم بعد. ابدأ أول اختبار لك!',
      testsCompleted: 'الاختبارات المكتملة',
      averageScore: 'المعدل المتوسط',
      dayStreak: 'سلسلة الأيام',
      examReady: 'جاهز للامتحان',
      recentPerformance: 'الأداء الأخير',
      focusAreas: 'مجالات التركيز',
      strongAreas: 'نقاط القوة',
      noWeakAreas: 'لا توجد نقاط ضعف.',
      noStrongAreas: 'لا توجد نقاط قوة بعد.',
      practiceWeakAreas: 'تدريب نقاط الضعف',
      takeMockExam: 'اختبار تجريبي',
      improving: 'يتحسن',
      declining: 'يتراجع',
    },
    testCategories: {
      hazardPerception: 'إدراك المخاطر',
      insightPractice: 'تدريب البصيرة',
      mandatorySigns: 'العلامات الإلزامية',
      warningSigns: 'علامات التحذير',
      prohibitorySigns: 'علامات الحظر',
      prohibitorySigns2: 'علامات الحظر 2',
      trafficLightsSignals: 'إشارات المرور',
      roadInformation: 'معلومات الطريق',
      signIdentification: 'تحديد العلامات',
      priorityRules: 'قواعد الأولوية',
      mandatorySigns2: 'العلامات الإلزامية 2',
      mockExam: 'اختبار تجريبي',
    },
    subscription: {
      title: 'أسعار بسيطة وصادقة',
      subtitle: 'ابدأ مجاناً. افتح المزيد من خلال المشاركة أو انتقل إلى Pro للحصول على قوة غير محدودة.',
      free: {
        name: 'مجاني',
        subtitle: 'الأفضل للتجربة',
        price: '€0',
        period: 'للأبد',
        feature1: '10 اختبارات تدريبية',
        feature2: '1 امتحان تجريبي قياسي',
        feature3: 'ميزات أساسية',
        currentPlan: 'الخطة الحالية',
        getStarted: 'ابدأ',
      },
      socialUnlocked: {
        name: 'مفتوح اجتماعياً',
        subtitle: 'شارك عن التطبيق',
        price: '€0',
        period: 'لمدة 7 أيام',
        feature1: 'اختبارات تدريبية غير محدودة',
        feature2: 'امتحان مخصص بالذكاء الاصطناعي غير محدود',
        feature3: 'كل شيء في المجاني',
        active: 'نشط',
        shareToUnlock: 'شارك لفتح 7 أيام مجاناً',
        note: 'بعد 7 أيام، تعود خطتك إلى المجاني.',
        socialUnlock: {
          title: 'افتح مفتوح اجتماعياً (7 أيام)',
          instruction1: 'شارك على Instagram أو TikTok ووسم @theorycoach',
          instruction2: 'احتفظ بالمنشور أو القصة مرئية لمدة 24 ساعة.',
          shareButton: 'مشاركة',
          note: 'بمجرد أن تشارك وتوسم @theorycoach، يتم فتح الميزات المميزة لمدة 7 أيام.',
        },
      },
      pro: {
        name: 'Pro',
        subtitle: 'للمتعلمين الجادين',
        mostPopular: 'الأكثر شعبية',
        oneMonth: 'شهر واحد',
        threeMonths: '3 أشهر',
        price1Month: '€15',
        price3Months: '€35',
        period1Month: 'شهرياً',
        period3Months: 'لمدة 3 أشهر',
        save: 'وفر €10',
        feature1: 'اختبارات تدريبية غير محدودة',
        feature2: 'امتحان مخصص بالذكاء الاصطناعي غير محدود',
        feature3: 'دعم ذو أولوية',
        feature4: 'كل شيء في المجاني',
        currentPlan: 'الخطة الحالية',
        upgrade1Month: 'ترقية إلى Pro مقابل €15/شهر',
        upgrade3Months: 'ترقية إلى Pro مقابل €35/3 أشهر',
      },
      footer: {
        text: 'تتضمن جميع الخطط الوصول إلى جميع الاختبارات التدريبية والميزات الأساسية',
        note: 'ألغِ في أي وقت. لا توجد رسوم مخفية.',
      },
      subscribe: 'اشترك',
      go: {
        name: 'Go',
        subtitle: 'وصول كامل - تجديد شهري',
        price: '€15',
        period: 'شهرياً، قابل للإلغاء في أي وقت',
        feature1: 'اختبارات ممارسة غير محدودة',
        feature2: 'امتحان مخصص بالذكاء الاصطناعي غير محدود',
        feature3: 'وصول غير محدود',
        currentPlan: 'الخطة الحالية',
        upgrade: 'ترقية إلى Go',
      },
      cookies: {
        title: 'نستخدم ملفات تعريف الارتباط',
        description: 'نستخدم ملفات تعريف الارتباط لتحسين تجربة التصفح الخاصة بك، وتقديم محتوى مخصص، وتحليل حركة المرور لدينا. بالنقر على "قبول الكل"، فإنك توافق على استخدامنا لملفات تعريف الارتباط.',
        customize: 'تخصيص',
        rejectAll: 'رفض الكل',
        acceptAll: 'قبول الكل',
        preferencesTitle: 'تفضيلات ملفات تعريف الارتباط',
        preferencesDescription: 'يمكنك اختيار ملفات تعريف الارتباط التي تريد قبولها. ملفات تعريف الارتباط الضرورية مطلوبة لعمل الموقع.',
        necessary: 'ملفات تعريف الارتباط الضرورية',
        necessaryDescription: 'مطلوبة لعمل الموقع. لا يمكن تعطيلها.',
        analytics: 'ملفات تعريف الارتباط التحليلية',
        analyticsDescription: 'تساعدنا على فهم كيفية تفاعل الزوار مع موقعنا.',
        marketing: 'ملفات تعريف الارتباط التسويقية',
        marketingDescription: 'تُستخدم لتقديم إعلانات مخصصة.',
        cancel: 'إلغاء',
        savePreferences: 'حفظ التفضيلات',
      },
    },
    voiceCommands: {
      optionA: 'أ',
      optionB: 'ب',
      optionC: 'ج',
      optionD: 'د',
      first: 'أول',
      second: 'ثاني',
      third: 'ثالث',
      fourth: 'رابع',
      one: 'واحد',
      two: 'اثنان',
      three: 'ثلاثة',
      four: 'أربعة',
    }
  }
};
